import { Globe, Edit, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { currencies, getDefaultCurrency } from "@/components/utils/currencies";
import AutocompleteSelect, { AutocompleteOption } from "@/components/ui/autocomplete-select";
import { Badge } from "@/components/ui/badge";
import EditableInput from "@/components/ui/editable-input";

// Language and currency section component with separate edit state
interface LanguageCurrencySectionProps {
  isEditing?: boolean;
  onEditToggle?: () => void;
  onCancelEdit?: () => void;
}

const LanguageCurrencySection = ({
  isEditing = false,
  onEditToggle,
  onCancelEdit
}: LanguageCurrencySectionProps) => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>("English");
  const [selectedCurrency, setSelectedCurrency] = useState<string>(getDefaultCurrency().code);

  // Converti le valute nel formato richiesto dal componente AutocompleteSelect
  const currencyOptions: AutocompleteOption[] = currencies.map(currency => ({
    value: currency.code,
    label: currency.code,
    description: currency.name,
    icon: currency.symbol
  }));

  const handleCurrencyChange = (currencyCode: string) => {
    setSelectedCurrency(currencyCode);
  };

  const getCurrencyDisplayValue = () => {
    const selectedCurrencyData = currencies.find(c => c.code === selectedCurrency);
    if (!selectedCurrencyData) return "";

    return `${selectedCurrencyData.symbol} ${selectedCurrencyData.code} - ${selectedCurrencyData.name}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-2">
            <Globe className="h-5 w-5" />
            Lingua e Valuta <Badge variant="secondary">soon</Badge>
          </h3>
          <p className="text-sm text-gray-600">
            Gestisci le tue preferenze di lingua e valuta
          </p>
        </div>
        {onEditToggle && onCancelEdit && (
          <div className="flex gap-2">
            {isEditing ? (
              <Button
                variant="outline"
                onClick={onCancelEdit}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Annulla
              </Button>
            ) : (
              <Button
                onClick={onEditToggle}
                className="flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                Modifica
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* Language Section */}
        <EditableInput
          label="Lingua"
          value={selectedLanguage}
          placeholder="Seleziona lingua..."
          isEditing={isEditing}
          onChange={setSelectedLanguage}
          disabled={true}
        />

        {/* Currency Section */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700">
            Valuta
          </Label>
          {isEditing ? (
            <AutocompleteSelect
              options={currencyOptions}
              value={selectedCurrency}
              onValueChange={handleCurrencyChange}
              placeholder="Seleziona valuta..."
              searchPlaceholder="Cerca valuta..."
              emptyMessage="Nessuna valuta trovata."
              disabled={true}
            />
          ) : (
            <div className="min-h-[34px] flex items-center">
              <div className="flex-1 text-gray-900">
                {getCurrencyDisplayValue()}
              </div>
            </div>
          )}
          {!isEditing && <div className="h-px bg-gray-300 w-full"></div>}
        </div>
      </div>
    </div>
  );
};

export default LanguageCurrencySection;
